import React from "react"
import { Container, Typography, Box, Paper, Link } from "@mui/material"

function PrivacyPage() {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ color: "primary.main", borderBottom: 2, borderColor: "divider", pb: 1 }}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 4 }}>
          Last Updated: July 2026
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Introduction
        </Typography>
        <Typography paragraph>
          TNKL ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and
          safeguard your information when you use our mobile application (the "App"). Please read this Privacy Policy carefully. By using our App, you
          consent to the data practices described in this policy.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Information We Collect
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          1. Account Information
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>
            <strong>Email Address</strong>: Required for account creation and authentication
          </li>
          <li>
            <strong>Password</strong>: Securely hashed and stored
          </li>
          <li>
            <strong>Display Name</strong>: Your chosen username or display name
          </li>
          <li>
            <strong>Profile Photo</strong>: Optional profile picture you upload
          </li>
          <li>
            <strong>User ID</strong>: Unique identifier assigned to your account
          </li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          2. Location Information
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>
            <strong>Current Location</strong>: We collect your device's location (latitude and longitude) when you:
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Search for nearby bathrooms</li>
              <li>Add new bathroom listings</li>
              <li>Enable location-based notifications</li>
              <li>Use map features</li>
            </Box>
          </li>
          <li>
            <strong>Location History</strong>: We may temporarily store location data to provide location-based services
          </li>
          <li>
            <strong>Address Information</strong>: Addresses you enter or select when adding bathroom listings
          </li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          3. User-Generated Content
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>
            <strong>Bathroom Listings</strong>: Information you provide when adding bathrooms, including:
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Bathroom name and description</li>
              <li>Photos of bathrooms</li>
              <li>Ratings and reviews</li>
              <li>Accessibility features</li>
              <li>Amenities and features</li>
            </Box>
          </li>
          <li>
            <strong>Reviews and Ratings</strong>: Reviews, ratings, comments, and photos you submit
          </li>
          <li>
            <strong>Chat Messages</strong>: Messages you send through the in-app chat feature
          </li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          4. Usage and Analytics Data
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>
            <strong>App Usage</strong>: Information about how you interact with the App
          </li>
          <li>
            <strong>Device Information</strong>: Device type, operating system, and app version
          </li>
          <li>
            <strong>Push Notification Tokens</strong>: To send you push notifications
          </li>
          <li>
            <strong>Analytics Data</strong>: Aggregated usage statistics
          </li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          5. Social Features Data
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>
            <strong>Following/Followers</strong>: Lists of users you follow and who follow you
          </li>
          <li>
            <strong>Notifications</strong>: Notifications about new bathrooms, reviews, and social interactions
          </li>
          <li>
            <strong>Gamification Data</strong>: Points, achievements, and leaderboard rankings
          </li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          How We Use Your Information
        </Typography>
        <Typography paragraph>We use the information we collect to:</Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          1. Provide and Improve Services
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Enable you to find and share bathroom locations</li>
          <li>Allow you to add, review, and rate bathrooms</li>
          <li>Provide location-based recommendations and notifications</li>
          <li>Facilitate communication between users through chat</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          2. Account Management
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Create and manage your account</li>
          <li>Authenticate your identity</li>
          <li>Personalize your experience</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          3. Social Features
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Enable following/followers functionality</li>
          <li>Send notifications about activities from users you follow</li>
          <li>Display leaderboards and achievements</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          4. Communication
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Send push notifications about nearby bathrooms</li>
          <li>Notify you of new reviews, messages, and social interactions</li>
          <li>Respond to your inquiries and support requests</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          5. Safety and Moderation
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Review and moderate user-generated content</li>
          <li>Handle reports of inappropriate content</li>
          <li>Enforce our Terms of Service</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          6. Analytics and Improvement
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Analyze app usage to improve functionality</li>
          <li>Understand user preferences and behavior</li>
          <li>Fix bugs and technical issues</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          How We Share Your Information
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Public Information
        </Typography>
        <Typography paragraph>The following information is publicly visible to all App users:</Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Your display name and profile photo</li>
          <li>Bathroom listings you create</li>
          <li>Reviews and ratings you submit</li>
          <li>Your points, achievements, and leaderboard position (if enabled)</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Limited Sharing
        </Typography>
        <Typography paragraph>We may share your information in the following circumstances:</Typography>
        <Box component="ol" sx={{ pl: 3 }}>
          <li>
            <strong>With Other Users</strong>: When you interact with other users (following, messaging, etc.)
          </li>
          <li>
            <strong>Service Providers</strong>: With third-party services that help us operate the App:
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                <strong>Google</strong>: For authentication, database, storage, and analytics
              </li>
              <li>
                <strong>Google Maps/Places API</strong>: For location services and address information
              </li>
              <li>
                <strong>Push Notification Services</strong>: For delivering notifications
              </li>
            </Box>
          </li>
          <li>
            <strong>Legal Requirements</strong>: When required by law, court order, or government regulation
          </li>
          <li>
            <strong>Safety and Security</strong>: To protect the rights, property, or safety of users or others
          </li>
          <li>
            <strong>Business Transfers</strong>: In connection with a merger, acquisition, or sale of assets
          </li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          We Do NOT Sell Your Data
        </Typography>
        <Typography paragraph>We do not sell, rent, or trade your personal information to third parties for their marketing purposes.</Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Data Storage and Security
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Storage
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Your data is stored securely using Google services</li>
          <li>Data is stored in the United States and may be processed in other countries where Google operates</li>
          <li>We retain your data for as long as your account is active or as needed to provide services</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Security Measures
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Passwords are encrypted and never stored in plain text</li>
          <li>We use industry-standard security measures to protect your data</li>
          <li>However, no method of transmission over the internet is 100% secure</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Your Rights and Choices
        </Typography>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Access and Control
        </Typography>
        <Typography paragraph>You have the right to:</Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>
            <strong>Access</strong>: View the personal information we hold about you
          </li>
          <li>
            <strong>Update</strong>: Correct or update your account information through the App
          </li>
          <li>
            <strong>Delete</strong>: Request deletion of your account and associated data
          </li>
          <li>
            <strong>Opt-Out</strong>: Disable location services, push notifications, or other features in Settings
          </li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Account Deletion
        </Typography>
        <Typography paragraph>To delete your account:</Typography>
        <Box component="ol" sx={{ pl: 3 }}>
          <li>Open the TNKL app and go to Profile > Settings</li>
          <li>Select "Delete Account" under the Account section and confirm your request</li>
          <li>We will delete your account and associated data within 30 days</li>
          <li>Note: Some information may be retained for legal or safety purposes</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Location Services
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>You can disable location services in your device settings</li>
          <li>Disabling location services will limit certain App features</li>
          <li>Location data is only collected when you actively use location-based features</li>
        </Box>

        <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 3 }}>
          Push Notifications
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>You can disable push notifications in the App Settings</li>
          <li>You can customize notification preferences (distance, types, etc.)</li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Children's Privacy
        </Typography>
        <Typography paragraph>
          Our App is not intended for users under the age of 13. We do not knowingly collect personal information from children under 13. If you
          believe we have collected information from a child under 13, please contact us immediately.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Third-Party Services
        </Typography>
        <Typography paragraph>Our App uses the following third-party services:</Typography>
        <Box component="ol" sx={{ pl: 3 }}>
          <li>
            <strong>Google Firebase</strong>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                Privacy Policy:{" "}
                <Link href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener">
                  https://firebase.google.com/support/privacy
                </Link>
              </li>
              <li>Used for: Authentication, database, storage, analytics</li>
            </Box>
          </li>
          <li>
            <strong>Google Maps/Places API</strong>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                Privacy Policy:{" "}
                <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener">
                  https://policies.google.com/privacy
                </Link>
              </li>
              <li>Used for: Maps, location services, address information</li>
            </Box>
          </li>
          <li>
            <strong>Expo</strong>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>
                Privacy Policy:{" "}
                <Link href="https://expo.dev/privacy" target="_blank" rel="noopener">
                  https://expo.dev/privacy
                </Link>
              </li>
              <li>Used for: App development and deployment platform</li>
            </Box>
          </li>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          International Users
        </Typography>
        <Typography paragraph>
          If you are using our App from outside the United States, please note that your information may be transferred to, stored, and processed in
          the United States and other countries where our service providers operate.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Changes to This Privacy Policy
        </Typography>
        <Typography paragraph>We may update this Privacy Policy from time to time. We will notify you of any changes by:</Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Posting the new Privacy Policy in the App</li>
          <li>Updating the "Last Updated" date</li>
          <li>Sending you a notification (for material changes)</li>
        </Box>
        <Typography paragraph>
          Your continued use of the App after changes become effective constitutes acceptance of the updated Privacy Policy.
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          Contact Us
        </Typography>
        <Typography paragraph>If you have questions about this Privacy Policy or our data practices, please contact us at:</Typography>
        <Typography paragraph>
          <strong>Email</strong>: <Link href="mailto:support@crappermapper.com">support@crappermapper.com</Link>
          <br />
          <strong>Website</strong>: <Link href="https://tnkl.app">https://tnkl.app</Link>
        </Typography>

        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
          California Privacy Rights
        </Typography>
        <Typography paragraph>
          If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>Right to know what personal information is collected</li>
          <li>Right to delete personal information</li>
          <li>Right to opt-out of the sale of personal information (we do not sell your data)</li>
          <li>Right to non-discrimination for exercising your privacy rights</li>
        </Box>
        <Typography paragraph>To exercise these rights, please contact us using the information above.</Typography>
      </Paper>
    </Container>
  )
}

export default PrivacyPage
